import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NetworkVisualization from './NetworkVisualization';
import AssociationRiskCard from './AssociationRiskCard';
import CentralityMetricsCard from './CentralityMetricsCard';
import ConnectedPersonsTable from './ConnectedPersonsTable';
import PersonDetailsSidePanel from './PersonDetailsSidePanel';
import {
  buildNetworkGraph,
  getNetworkAnalysis,
  getNetworkVisualization,
  calculateAssociationRisk,
} from '../../../services/networkIntelligence.service';

/**
 * Main panel component for Network Intelligence features.
 * Combines visualization, risk assessment, centrality metrics, and connections.
 * Includes investigation features with side panel for person details.
 */
const NetworkIntelligencePanel = ({ alienId }) => {
  const navigate = useNavigate();

  // State
  const [analysis, setAnalysis] = useState(null);
  const [visualization, setVisualization] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBuildingNetwork, setIsBuildingNetwork] = useState(false);
  const [error, setError] = useState(null);
  const [buildResult, setBuildResult] = useState(null);
  const [maxDepth, setMaxDepth] = useState(2);

  // Side panel state
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [selectedConnectionInfo, setSelectedConnectionInfo] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  // Load analysis when alienId changes
  useEffect(() => {
    if (alienId) {
      loadNetworkData();
    }
  }, [alienId]);

  const loadNetworkData = useCallback(async (forceRefresh = false) => {
    if (!alienId) {
      setError('Please enter a Person ID');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Load analysis and visualization in parallel
      // Always force refresh to get latest network data
      const [analysisData, vizData] = await Promise.all([
        getNetworkAnalysis(alienId, forceRefresh),
        getNetworkVisualization(alienId, maxDepth),
      ]);

      if (analysisData && !analysisData.error) {
        setAnalysis(analysisData);
      } else if (analysisData?.error) {
        setError(analysisData.error);
      }

      if (vizData && vizData.nodes?.length > 0) {
        setVisualization(vizData);
      }
    } catch (err) {
      setError('Failed to load network data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [alienId, maxDepth]);

  const handleBuildNetwork = useCallback(async () => {
    setIsBuildingNetwork(true);
    setError(null);
    setBuildResult(null);

    try {
      const result = await buildNetworkGraph({
        timeWindow: '1h',      // 1 hour window (more lenient)
        precision: 2,          // ~1km area (wider matching)
        minInteractions: 1,    // Single co-location creates edge
      });

      if (result && result.status === 'success') {
        setBuildResult(result);
        // Reload data after building with force refresh to bypass cache
        if (alienId) {
          await loadNetworkData(true);
        }
      } else {
        setError('Failed to build network graph');
      }
    } catch (err) {
      setError('Failed to build network graph');
      console.error(err);
    } finally {
      setIsBuildingNetwork(false);
    }
  }, [alienId, loadNetworkData]);

  const handleRecalculateRisk = useCallback(async () => {
    if (!alienId) return;

    setIsLoading(true);
    try {
      const riskData = await calculateAssociationRisk(alienId);
      if (riskData && analysis) {
        setAnalysis({
          ...analysis,
          associationRisk: riskData,
        });
      }
    } catch (err) {
      console.error('Failed to recalculate risk:', err);
    } finally {
      setIsLoading(false);
    }
  }, [alienId, analysis]);

  // Handle person selection from graph or table
  const handlePersonSelect = useCallback((personId, connectionInfo = null) => {
    setSelectedPersonId(personId);
    setSelectedConnectionInfo(connectionInfo);
    setIsSidePanelOpen(true);
  }, []);

  // Handle closing the side panel
  const handleCloseSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
    setSelectedPersonId(null);
    setSelectedConnectionInfo(null);
  }, []);

  // Handle navigation to person profile
  const handleNavigateToPerson = useCallback((personId) => {
    // Navigate to the person's profile page
    // Adjust this route based on your app's routing structure
    navigate(`/applicant/${personId}`);
  }, [navigate]);

  const handleNodeClick = useCallback((node) => {
    console.log('Node clicked:', node);
  }, []);

  const handlePersonClick = useCallback((person) => {
    console.log('Person clicked:', person);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold">Network Intelligence</h2>
            <p className="text-sm text-gray-500">
              Analyze subject associations and network-based risk
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleBuildNetwork}
              disabled={isBuildingNetwork}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isBuildingNetwork ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Building...
                </span>
              ) : (
                'Build Network Graph'
              )}
            </button>
            <button
              onClick={() => loadNetworkData(true)}
              disabled={isLoading || !alienId}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              title="Force refresh analysis (bypass cache)"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Analyzing...
                </span>
              ) : (
                'Analyze Network'
              )}
            </button>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Depth:</label>
              <select
                value={maxDepth}
                onChange={(e) => setMaxDepth(parseInt(e.target.value))}
                className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={1}>1 (Direct only)</option>
                <option value={2}>2 (Default)</option>
                <option value={3}>3 (Extended)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Build Result */}
        {buildResult && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
            <div className="font-medium text-green-800">Network Built Successfully</div>
            <div className="text-green-700 mt-1">
              {buildResult.totalNodes} nodes, {buildResult.totalEdges} edges, {buildResult.communitiesFound} communities
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Network Summary */}
      {analysis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-gray-500 text-sm">Network Nodes</div>
            <div className="text-2xl font-bold">{analysis.totalNodes || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-gray-500 text-sm">Network Edges</div>
            <div className="text-2xl font-bold">{analysis.totalEdges || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-gray-500 text-sm">Communities</div>
            <div className="text-2xl font-bold">{analysis.communitiesFound || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-gray-500 text-sm">Connections</div>
            <div className="text-2xl font-bold">{analysis.connectedPersons?.length || 0}</div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Visualization */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-3">Network Graph</h3>
            <NetworkVisualization
              data={visualization}
              width={700}
              height={450}
              onNodeClick={handleNodeClick}
              onNodeSelect={handlePersonSelect}
            />
            {visualization && visualization.nodes?.length > 0 && (
              <div className="mt-3 text-xs text-gray-500">
                <strong>Tip:</strong> Hover over nodes to see person details. Click to open the investigation panel.
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Risk & Metrics */}
        <div className="space-y-6">
          <AssociationRiskCard riskData={analysis?.associationRisk} />
          <CentralityMetricsCard metrics={analysis?.centralityMetrics} />
        </div>
      </div>

      {/* Connected Persons Table */}
      {analysis?.connectedPersons && analysis.connectedPersons.length > 0 && (
        <ConnectedPersonsTable
          connectedPersons={analysis.connectedPersons}
          onPersonClick={handlePersonClick}
          onPersonSelect={handlePersonSelect}
          focalPersonId={alienId}
        />
      )}

      {/* No Data Message */}
      {!isLoading && !analysis && !error && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Network Data</h3>
          <p className="text-gray-500 mb-4">
            Build the network graph from co-location data to analyze subject associations.
          </p>
          <button
            onClick={handleBuildNetwork}
            disabled={isBuildingNetwork}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Build Network Graph
          </button>
        </div>
      )}

      {/* Person Details Side Panel */}
      {isSidePanelOpen && selectedPersonId && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={handleCloseSidePanel}
          />
          {/* Panel */}
          <PersonDetailsSidePanel
            personId={selectedPersonId}
            connectionInfo={selectedConnectionInfo}
            onClose={handleCloseSidePanel}
            onNavigate={handleNavigateToPerson}
          />
        </>
      )}
    </div>
  );
};

export default NetworkIntelligencePanel;
